import React, { useMemo } from 'react'
import styled from 'styled-components'

import PaneBody from 'components/Common/Pane/PaneBody'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import Pluralize from 'components/Common/Pluralize'

const TwoColumns = styled.div`
  margin-top: ${rem(20)};

  > * + * {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const DetailLine = styled.div`
  margin-top: ${rem(20)};
`

const PaneDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  > * + * {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: ${rem(20)};

    > * + * {
      margin-top: 0;
    }
  }
`

const ViewRoomPhotos = styled.div`
  ${mediaQueryUp.tablet} {
    grid-column-start: 2;
  }
`

function getMaxRoomCapacity(capacities: App.BedbankPackageCapacities) {
  let maxTotal = 0
  let maxAdults = 0
  let maxChildren = 0

  for (const capacity of capacities.combinations) {
    if (capacity.adults > maxAdults) {
      maxAdults = capacity.adults
    }
    if (capacity.children > maxChildren) {
      maxChildren = capacity.children
    }
    const total = capacity.adults + capacity.children
    if (total > maxTotal) {
      maxTotal = total
    }
  }

  return { total: maxTotal, adults: maxAdults, children: maxChildren }
}

interface Props {
  pkg: App.BedbankPackage;
  onCarouselToggle?: () => void;
}

function DetailsSection({
  pkg,
  onCarouselToggle,
}: React.PropsWithChildren<Props>) {
  const maxCapacities = useMemo(() => getMaxRoomCapacity(pkg.capacities), [pkg.capacities])

  return <PaneBody size="lg xl-y">
    <PaneDetailsWrapper>
      <div>
        <BodyText variant="large" weight="bold">About the room:</BodyText>
        <DetailLine>
          <BodyText variant="medium" weight="bold" as="span">Maximum room capacity: </BodyText>
          <BodyText variant="medium" as="span">
            <Pluralize count={maxCapacities.total} singular="guest"/>
            {!!maxCapacities.children && ` (up to ${pluralizeToString('adult', maxCapacities.adults)}, up to ${pluralizeToString('child', maxCapacities.children)})`}
          </BodyText>
        </DetailLine>
        {pkg.bedGroupsDescription && (
          <DetailLine>
            <BodyText variant="medium" weight="bold" as="span">Bed type: </BodyText>
            <BodyText variant="medium" as="span">{pkg.bedGroupsDescription}</BodyText>
          </DetailLine>
        )}
      </div>
      <div>
        <BodyText variant="large" weight="bold">Room facilities:</BodyText>
        <TwoColumns>
          {pkg.facilityGroups.map(group => (
            <div key={group.name}>
              <BodyText variant="medium" weight="bold">{group.name}</BodyText>
              {group.values.map(value => <BodyText variant="medium" key={value.name}>{value.name}</BodyText>)}
            </div>
          ))}
        </TwoColumns>
      </div>
      { onCarouselToggle && <ViewRoomPhotos>
        <TextButton kind="tertiary" outdent="left" startIcon={<LineCameraIcon />} onClick={onCarouselToggle}>
          View room photos
        </TextButton>
      </ViewRoomPhotos>}
    </PaneDetailsWrapper>
  </PaneBody>
}

export default DetailsSection
